<template>
  <div class="passenger-info">
    <div class="name-row">
      <div class="field">
        <label class="field-label">Passport First Name <span class="req">*</span></label>
        <input type="text" class="field-input" :class="{ 'has-error': errors.first_name }" :value="value.first_name" @input="update('first_name', $event.target.value)" @blur="touch('first_name')" placeholder="Jane" />
        <p v-if="errors.first_name" class="field-error">{{ errors.first_name }}</p>
      </div>
      <div class="field">
        <label class="field-label">Last Name <span class="req">*</span></label>
        <input type="text" class="field-input" :class="{ 'has-error': errors.last_name }" :value="value.last_name" @input="update('last_name', $event.target.value)" @blur="touch('last_name')" placeholder="Doe" />
        <p v-if="errors.last_name" class="field-error">{{ errors.last_name }}</p>
      </div>
    </div>

    <div class="field" v-if="!hideEmail">
      <label class="field-label">Email <span class="req">*</span></label>
      <input type="email" class="field-input" :class="{ 'has-error': errors.email }" :value="value.email" @input="update('email', $event.target.value)" @blur="touch('email')" placeholder="jane@example.com" />
      <p v-if="errors.email" class="field-error">{{ errors.email }}</p>
    </div>

    <div class="field">
      <label class="field-label">{{ $t('mobile') }} <span class="req">*</span></label>
      <div class="phone-row">
        <PhonePrefixSelect class="phone-prefix" :value="value.mobile_prefix || '+61'" @change="update('mobile_prefix', $event)" />
        <input type="tel" class="field-input phone-number" :class="{ 'has-error': errors.mobile_number }" :value="value.mobile_number" @input="update('mobile_number', $event.target.value)" @blur="touch('mobile_number')" placeholder="412 345 678" />
      </div>
      <p v-if="errors.mobile_number" class="field-error">{{ errors.mobile_number }}</p>
    </div>

    <div class="name-row identity-row">
      <div class="field">
        <label class="field-label">{{ $t('passport') }}</label>
        <input
          type="text"
          class="field-input"
          :class="{ 'has-error': errors.passport || errors.identity }"
          :value="value.passport"
          @input="onPassportInput($event.target.value)"
          @blur="touch('passport')"
          :placeholder="$t('passport')"
        />
        <p v-if="errors.passport" class="field-error">{{ errors.passport }}</p>
      </div>
      <div class="field">
        <label class="field-label">{{ $t('licence') }}</label>
        <input
          type="text"
          class="field-input"
          :class="{ 'has-error': errors.licence || errors.identity }"
          :value="value.licence"
          @input="onLicenceInput($event.target.value)"
          @blur="touch('licence')"
          :placeholder="$t('licence')"
        />
        <p v-if="errors.licence" class="field-error">{{ errors.licence }}</p>
      </div>
    </div>
    <p v-if="requireIdentity" class="identity-hint">{{ $t('eitherPassportOrLicence') }}</p>
    <p v-if="errors.identity" class="field-error identity-error">{{ errors.identity }}</p>

    <div class="name-row">
      <div class="field">
        <label class="field-label">Flight Number</label>
        <input type="text" class="field-input" :value="value.flight_no" @input="update('flight_no', $event.target.value)" placeholder="QF123" />
      </div>
      <div class="field">
        <label class="field-label">Travel Date</label>
        <input type="date" class="field-input" :value="value.travel_date" @input="update('travel_date', $event.target.value)" />
      </div>
    </div>
  </div>
</template>

<script>
import PhonePrefixSelect from './PhonePrefixSelect.vue'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default {
  name: 'PassengerInfo',
  components: {
    PhonePrefixSelect
  },
  props: {
    value: {
      type: Object,
      default: () => ({
        first_name: '', last_name: '', email: '',
        mobile_prefix: '+61', mobile_number: '',
        passport: '', licence: '', flight_no: '', travel_date: ''
      })
    },
    hideEmail: {
      type: Boolean,
      default: false
    },
    requireIdentity: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      touched: {}
    }
  },
  computed: {
    passportValue () {
      return String(this.value.passport || '').trim()
    },
    licenceValue () {
      return String(this.value.licence || '').trim()
    },
    errors () {
      const e = {}
      if (this.touched.first_name && !this.value.first_name) e.first_name = 'First name ' + this.$t('required')
      if (this.touched.last_name && !this.value.last_name) e.last_name = 'Last name ' + this.$t('required')
      if (!this.hideEmail && this.touched.email) {
        if (!this.value.email) e.email = 'Email ' + this.$t('required')
        else if (!EMAIL_RE.test(this.value.email)) e.email = 'Enter a valid email.'
      }
      if (this.touched.mobile_number && !this.value.mobile_number) e.mobile_number = this.$t('mobile') + ' ' + this.$t('required')

      if (this.requireIdentity && this.touched.identity) {
        const hasPassport = this.passportValue !== ''
        const hasLicence = this.licenceValue !== ''
        if (!hasPassport && !hasLicence) {
          e.identity = this.$t('eitherPassportOrLicence')
        } else if (hasPassport && hasLicence) {
          e.identity = this.$t('identityExclusive')
        }
      }

      return e
    }
  },
  methods: {
    update (key, val) {
      var next = Object.assign({}, this.value)
      next[key] = val
      this.$emit('change', next)
    },
    onPassportInput (val) {
      const next = Object.assign({}, this.value, { passport: val })
      if (this.requireIdentity && String(val || '').trim() !== '') {
        next.licence = ''
      }
      this.$emit('change', next)
      this.touch('passport')
    },
    onLicenceInput (val) {
      const next = Object.assign({}, this.value, { licence: val })
      if (this.requireIdentity && String(val || '').trim() !== '') {
        next.passport = ''
      }
      this.$emit('change', next)
      this.touch('licence')
    },
    touch (key) {
      this.$set(this.touched, key, true)
      if (key === 'passport' || key === 'licence') this.$set(this.touched, 'identity', true)
    },
    validate () {
      const touched = {
        first_name: true,
        last_name: true,
        mobile_number: true
      }
      if (!this.hideEmail) touched.email = true
      if (this.requireIdentity) touched.identity = true
      this.touched = touched
      return Object.keys(this.errors).length === 0
    }
  }
}
</script>

<style scoped>
.passenger-info { margin-bottom: 20px; }
.name-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.field { margin-bottom: 14px; }
.field-label { display: block; font-size: 13px; font-weight: 600; color: #1e336f; margin-bottom: 6px; }
.req { color: #ef4444; }
.field-input {
  width: 100%; min-height: 44px; padding: 10px 12px; font-size: 15px;
  border: 1px solid #e8ecf1; border-radius: 8px; background: #fff; color: #1e336f;
  outline: none; transition: border-color .2s;
}
.field-input:focus { border-color: #1e336f; box-shadow: 0 0 0 3px rgba(30,51,111,.12); }
.field-input.has-error { border-color: #ef4444; }
.phone-row { display: flex; gap: 8px; }
.phone-prefix { flex: 5 1 0; min-width: 0; }
.phone-number { flex: 6 1 0; min-width: 0; }
.field-error { margin-top: 4px; font-size: 12px; color: #ef4444; }
.identity-hint { margin: -4px 0 8px; font-size: 12px; color: #6b7280; line-height: 1.45; }
.identity-error { margin-top: -4px; margin-bottom: 8px; }
@media (max-width: 480px) { .name-row { grid-template-columns: 1fr; } }
</style>



// WEBPACK FOOTER //
// src/components/PassengerInfo.vue